import { useEffect, useMemo, useState, type FormEvent } from 'react'
import { useSearchParams } from 'react-router-dom'
import { api, ApiFailure, type PlatformClinic } from '../../api'
import { usePlatform } from './PlatformLayout'
import { Alert, Badge, Drawer, Empty, Icon, Modal, P, PageHeader, ago, day, fmt, initials, toneFor } from './ui'

/* Every clinic on the deployment: find one, open it, add one, suspend or
   reactivate it. Counts only - a platform admin never sees a clinic's records. */

const blank = { name: '', slug: '', city: '', phone: '', adminFullName: '', adminUsername: '', adminPassword: '' }

/** "Sunrise Family Clinic, Pune" -> "sunrise-family-clinic-pune" */
function toSlug(name: string): string {
  return name.toLowerCase().normalize('NFKD').replace(/['’]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 40)
}

/** A first password the admin will be made to change at first sign-in. */
function makePassword(): string {
  const words = ['Neem', 'Tulsi', 'Lotus', 'Mango', 'Banyan', 'Jasmine', 'Peepal', 'Saffron']
  const n = crypto.getRandomValues(new Uint32Array(2))
  return `${words[n[0]! % words.length]}@${1000 + (n[1]! % 9000)}`
}

type Filter = 'all' | 'active' | 'suspended'

export default function Clinics() {
  const { clinics, reload } = usePlatform()
  const [params, setParams] = useSearchParams()
  const [filter, setFilter] = useState<Filter>('all')
  const [q, setQ] = useState(params.get('q') ?? '')
  const [msg, setMsg] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null)
  const [busy, setBusy] = useState(false)
  const creating = params.get('new') === '1'
  const openId = params.get('open')
  const open = clinics?.find((c) => c.id === openId) ?? null
  const [confirm, setConfirm] = useState<PlatformClinic | null>(null)
  const [form, setForm] = useState(blank)
  const [slugTouched, setSlugTouched] = useState(false)
  const [rename, setRename] = useState('')

  useEffect(() => { setQ(params.get('q') ?? '') }, [params])
  useEffect(() => { setRename(open?.name ?? '') }, [open?.id, open?.name])

  function setParam(key: string, value: string | null) {
    setParams((prev) => { const p = new URLSearchParams(prev); if (value === null) p.delete(key); else p.set(key, value); return p })
  }

  const term = q.trim().toLowerCase()
  const shown = useMemo(() => (clinics ?? []).filter((c) =>
    (filter === 'all' || c.status === filter)
    && (!term || `${c.name} ${c.slug} ${c.admins ?? ''}`.toLowerCase().includes(term))), [clinics, filter, term])
  const count = (f: Filter) => (clinics ?? []).filter((c) => f === 'all' || c.status === f).length

  const set = (k: keyof typeof blank) => (v: string) => setForm((f) => {
    const next = { ...f, [k]: v }
    if (k === 'name' && !slugTouched) next.slug = toSlug(v)
    return next
  })

  async function create(e: FormEvent) {
    e.preventDefault()
    setBusy(true); setMsg(null)
    try {
      const r = await api.post<{ clinic: PlatformClinic }>('/api/platform/clinics', { ...form, city: form.city || null, phone: form.phone || null })
      setMsg({ kind: 'ok', text: `${r.clinic.name} is ready. Its admin signs in as “${form.adminUsername}” with the first password you set, and is asked to change it.` })
      setForm(blank); setSlugTouched(false); setParam('new', null)
      await reload()
    } catch (err) {
      setMsg({ kind: 'error', text: err instanceof ApiFailure ? err.message : 'Could not create the clinic.' })
    } finally {
      setBusy(false)
    }
  }

  async function patch(c: PlatformClinic, body: { status?: 'active' | 'suspended'; name?: string }, ok: string) {
    setBusy(true); setMsg(null)
    try {
      await api.patch(`/api/platform/clinics/${c.id}`, body)
      setMsg({ kind: 'ok', text: ok }); setConfirm(null)
      await reload()
    } catch (err) {
      setMsg({ kind: 'error', text: err instanceof ApiFailure ? err.message : 'Could not change the clinic.' })
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <PageHeader crumbs={['Platform', 'Tenants', 'Clinics']} title="Clinics"
        description="Every clinic on this server. Each one sees only its own patients, staff and records."
        actions={<button type="button" className="pa-btn pa-btn-primary" onClick={() => { setMsg(null); setParam('new', '1') }}><Icon d={P.plus} />Add clinic</button>} />
      {msg && <Alert kind={msg.kind} onClose={() => setMsg(null)}>{msg.text}</Alert>}

      <div className="pa-card">
        <div className="pa-toolbar">
          <div className="pa-tabs" role="tablist" aria-label="Status">
            {(['all', 'active', 'suspended'] as Filter[]).map((f) => (
              <button key={f} type="button" role="tab" aria-selected={filter === f} className={filter === f ? 'on' : ''} onClick={() => setFilter(f)}>
                {f === 'all' ? 'All' : f === 'active' ? 'Active' : 'Suspended'}<b>{count(f)}</b>
              </button>
            ))}
          </div>
          <label className="pa-search" style={{ marginLeft: 'auto', width: 280, background: 'hsl(var(--surface))', borderColor: 'hsl(var(--border))' }}>
            <Icon d={P.search} size={14} />
            <input aria-label="Filter clinics" placeholder="Filter by name, handle or admin" value={q}
              onChange={(e) => { setQ(e.target.value); setParam('q', e.target.value || null) }} />
          </label>
        </div>
        <div className="pa-table-wrap">
          <table className="pa-table">
            <thead>
              <tr><th>Clinic</th><th>Status</th><th>Admins</th><th className="num">Staff</th><th className="num">Patients</th><th className="num">Visits · 30d</th><th>Last visit</th><th>Created</th></tr>
            </thead>
            <tbody>
              {clinics === null && [0, 1, 2].map((i) => <tr key={i}><td colSpan={8}><div className="pa-skel" style={{ height: 22 }} /></td></tr>)}
              {shown.map((c) => (
                <tr key={c.id} className="click" tabIndex={0} onClick={() => setParam('open', c.id)}
                  onKeyDown={(e) => { if (e.key === 'Enter') setParam('open', c.id) }}>
                  <td><div className="pa-cell"><span className={`pa-avatar t-${toneFor(c.name)}`}>{initials(c.name)}</span><div><b>{c.name}</b><small className="pa-code">{c.slug}</small></div></div></td>
                  <td>{c.status === 'active' ? <Badge tone="success" dot>Active</Badge> : <Badge tone="danger" dot>Suspended</Badge>}</td>
                  <td>{c.admins ?? <span className="subtle">none</span>}</td>
                  <td className="num">{fmt(c.staff)}</td>
                  <td className="num">{fmt(c.patients)}</td>
                  <td className="num">{fmt(c.visitsLast30Days)}</td>
                  <td className="muted" style={{ whiteSpace: 'nowrap' }}>{day(c.lastVisit)}</td>
                  <td className="muted" style={{ whiteSpace: 'nowrap' }}>{day(c.createdAt)}</td>
                </tr>
              ))}
              {clinics && shown.length === 0 && (
                <tr><td colSpan={8}>
                  {clinics.length === 0
                    ? <Empty icon={P.building} title="No clinics yet" action={<button type="button" className="pa-btn pa-btn-primary" onClick={() => setParam('new', '1')}><Icon d={P.plus} />Add the first clinic</button>}>Add a clinic and its first admin to get started.</Empty>
                    : <Empty icon={P.search} title="No clinics match">Try another name, or clear the filter.</Empty>}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {open && (
        <Drawer title={open.name} subtitle={<span className="pa-code">{open.slug}</span>} onClose={() => setParam('open', null)}
          footer={open.status === 'active'
            ? <button type="button" className="pa-btn pa-btn-danger" disabled={busy} onClick={() => setConfirm(open)}><Icon d={P.pause} />Suspend clinic</button>
            : <button type="button" className="pa-btn pa-btn-primary" disabled={busy} onClick={() => void patch(open, { status: 'active' }, `${open.name} is active again.`)}><Icon d={P.play} />Reactivate</button>}>
          <div>{open.status === 'active' ? <Badge tone="success" dot>Active</Badge> : <Badge tone="danger" dot>Suspended</Badge>}</div>
          <div className="pa-mini-kpis">
            <div><b>{fmt(open.patients)}</b><small>Patients</small></div>
            <div><b>{fmt(open.visitsLast30Days)}</b><small>Visits · 30d</small></div>
            <div><b>{fmt(open.staff)}</b><small>Staff</small></div>
          </div>
          <dl className="pa-dl">
            <dt>Admins</dt><dd>{open.admins ?? '—'}</dd>
            <dt>Last visit</dt><dd>{day(open.lastVisit)}</dd>
            <dt>Created</dt><dd>{day(open.createdAt)} · {ago(open.createdAt)}</dd>
            <dt>Clinic ID</dt><dd className="pa-code" style={{ wordBreak: 'break-all' }}>{open.id}</dd>
          </dl>
          <form className="pa-field" onSubmit={(e) => { e.preventDefault(); if (rename.trim() && rename.trim() !== open.name) void patch(open, { name: rename.trim() }, 'Name saved.') }}>
            <label htmlFor="pa-rename">Clinic name</label>
            <div style={{ display: 'flex', gap: 8 }}>
              <input id="pa-rename" className="pa-input" value={rename} onChange={(e) => setRename(e.target.value)} />
              <button type="submit" className="pa-btn" disabled={busy || !rename.trim() || rename.trim() === open.name}>Save</button>
            </div>
            <span className="hint">What the platform calls it. The clinic sets the name its patients see in its own settings.</span>
          </form>
        </Drawer>
      )}

      {creating && (
        <Modal title="Add a clinic" description="The clinic gets its own settings, the starter drug and test lists, and one admin login."
          onClose={() => setParam('new', null)}
          footer={<>
            <button type="button" className="pa-btn" onClick={() => setParam('new', null)}>Cancel</button>
            <button type="submit" form="pa-new" className="pa-btn pa-btn-primary" disabled={busy}>{busy ? 'Creating…' : 'Create clinic'}</button>
          </>}>
          <form id="pa-new" className="pa-form-grid" onSubmit={(e) => void create(e)}>
            <div className="pa-field wide"><label htmlFor="pc-name">Clinic name</label>
              <input id="pc-name" className="pa-input" required autoFocus value={form.name} onChange={(e) => set('name')(e.target.value)} placeholder="Lotus Children’s Clinic" /></div>
            <div className="pa-field wide"><label htmlFor="pc-slug">Handle</label>
              <input id="pc-slug" className="pa-input pa-code" required value={form.slug} onChange={(e) => { setSlugTouched(true); set('slug')(e.target.value) }} />
              <span className="hint">Lowercase letters, numbers and hyphens. Unique on this server.</span></div>
            <div className="pa-field"><label htmlFor="pc-city">City</label><input id="pc-city" className="pa-input" value={form.city} onChange={(e) => set('city')(e.target.value)} /></div>
            <div className="pa-field"><label htmlFor="pc-phone">Phone</label><input id="pc-phone" className="pa-input" value={form.phone} onChange={(e) => set('phone')(e.target.value)} /></div>
            <div className="pa-field wide" style={{ borderTop: '1px solid hsl(var(--border))', paddingTop: 14 }}><b style={{ fontSize: 13 }}>First admin</b></div>
            <div className="pa-field"><label htmlFor="pc-admin">Full name</label><input id="pc-admin" className="pa-input" required value={form.adminFullName} onChange={(e) => set('adminFullName')(e.target.value)} /></div>
            <div className="pa-field"><label htmlFor="pc-user">Username</label>
              <input id="pc-user" className="pa-input" required autoComplete="off" value={form.adminUsername} onChange={(e) => set('adminUsername')(e.target.value.toLowerCase())} />
              <span className="hint">Unique across every clinic.</span></div>
            <div className="pa-field wide"><label htmlFor="pc-pass">First password</label>
              <div style={{ display: 'flex', gap: 8 }}>
                <input id="pc-pass" className="pa-input pa-code" required autoComplete="new-password" value={form.adminPassword} onChange={(e) => set('adminPassword')(e.target.value)} />
                <button type="button" className="pa-btn" onClick={() => set('adminPassword')(makePassword())}>Generate</button>
              </div>
              <span className="hint">They are asked to change it at first sign-in.</span></div>
          </form>
        </Modal>
      )}

      {confirm && (
        <Modal title={`Suspend ${confirm.name}?`} onClose={() => setConfirm(null)}
          footer={<>
            <button type="button" className="pa-btn" onClick={() => setConfirm(null)}>Cancel</button>
            <button type="button" className="pa-btn pa-btn-danger" disabled={busy}
              onClick={() => void patch(confirm, { status: 'suspended' }, `${confirm.name} is suspended. Its people were signed out.`)}>Suspend clinic</button>
          </>}>
          <p style={{ fontSize: 13 }} className="muted">Everyone at this clinic is signed out now and cannot sign in until you reactivate it. Nothing is deleted: its {fmt(confirm.patients)} patient records stay exactly as they are.</p>
        </Modal>
      )}
    </>
  )
}
